import { isRepeat, isWithin, type Point, type Rect } from './hitTest';

/**
 * The dwell state machine, as a pure reducer.
 *
 * Pure for the same reason `hitTest.ts` is: the behaviour here is *timing*, and timing asserted
 * through a browser is asserted through a scheduler nobody controls. Every rule below can be stated
 * as an input and an expected outcome, and the adapter around it is then only plumbing.
 *
 * ── What dwell means on an iPad ──
 *
 * There is no hover on iPadOS, so this is **press and hold in place**, not hover-to-select. That is
 * what 3.1's acceptance describes — "select without needing a clean tap-and-release" — and it is the
 * right primitive for someone who can reach a target but cannot reliably lift cleanly off it.
 *
 * ── Three decisions that carry the whole design ──
 *
 * **1. Movement inside `slopPx` does not restart the timer.** It is tempting to treat any wobble as
 * "still settling" and start again, and it would make dwell unusable for the person it is for: a
 * tremoring finger would reset the clock forever and never select anything. Slop means *do not
 * cancel*; it does not mean *start over*.
 *
 * **2. After a completed dwell the press must be released before another can begin.** Without this,
 * a finger held down and dragged across the keyboard would type a letter every `dwellMs` — which is
 * exactly the failure 3.1's acceptance names, "no accidental activations from resting a finger while
 * moving". So completion goes to `held`, not `idle`.
 *
 * **3. Tremor suppression applies here too**, per the phase gotcha: "dwell plus tremor can cause
 * repeated activation on re-entry — enforce `repeatSuppressMs` in dwell mode too, not just direct
 * touch." Re-entering the same target within the window is suppressed, and the window is per target
 * class exactly as in `DirectTouch` — a key gets the short one, an action gets the long one.
 */

export type DwellState =
  | { kind: 'idle' }
  | {
      kind: 'dwelling';
      targetId: string;
      rect: Rect;
      /** When the press started. Not moved by wobble — see decision 1. */
      startedAtMs: number;
    }
  /** Dwell completed, or was cancelled, and the finger is still down. Waiting for release. */
  | { kind: 'held' };

export type DwellEvent =
  | { kind: 'down'; targetId: string | null; rect: Rect | null; point: Point; atMs: number }
  | { kind: 'move'; point: Point; atMs: number }
  | { kind: 'tick'; atMs: number }
  | { kind: 'up'; atMs: number };

export type DwellEffect =
  | { kind: 'activate'; targetId: string }
  | { kind: 'suppressed'; targetId: string }
  /**
   * Reported rather than swallowed, because a cancellation *rate* is the tremor signal — the thing
   * that would tell a caregiver `dwellMs` is set too long for today. `DirectTouch` records
   * `ActivationMissed` for the same reason.
   */
  | { kind: 'cancelled'; targetId: string; reason: 'moved' | 'released' };

export interface DwellConfig {
  dwellMs: number;
  slopPx: number;
  /** Suppression window for this target, already resolved by class. */
  repeatSuppressMs: number;
}

export interface DwellHistory {
  lastTargetId: string | null;
  lastAtMs: number | null;
}

export interface DwellResult {
  state: DwellState;
  effect: DwellEffect | null;
}

export const idleDwell = (): DwellState => ({ kind: 'idle' });

export function reduceDwell(
  state: DwellState,
  event: DwellEvent,
  config: DwellConfig,
  history: DwellHistory,
): DwellResult {
  switch (event.kind) {
    case 'down': {
      // **The first finger wins.** A press arriving while one is already in progress is ignored, which
      // matters more here than it would elsewhere: a resting palm or a steadying hand puts extra
      // pointers on the glass, and this person may well need to brace against the device to reach it.
      // Without this, the second contact would take over the dwell the first one was part-way through.
      if (state.kind !== 'idle') return { state, effect: null };

      // A press on nothing interactive. `held` rather than `idle` so that sliding from empty space onto
      // a key cannot start a dwell — the person has to lift and place.
      if (event.targetId === null || event.rect === null) {
        return { state: { kind: 'held' }, effect: null };
      }

      if (
        isRepeat({
          targetId: event.targetId,
          atMs: event.atMs,
          lastTargetId: history.lastTargetId,
          lastAtMs: history.lastAtMs,
          repeatSuppressMs: config.repeatSuppressMs,
        })
      ) {
        // The gotcha's case: a tremor lifts and re-lands on the same key. Suppressed, and `held` so
        // the same press cannot try again by holding still.
        return { state: { kind: 'held' }, effect: { kind: 'suppressed', targetId: event.targetId } };
      }

      return {
        state: {
          kind: 'dwelling',
          targetId: event.targetId,
          rect: event.rect,
          startedAtMs: event.atMs,
        },
        effect: null,
      };
    }

    case 'move': {
      if (state.kind !== 'dwelling') return { state, effect: null };

      // Inside slop: keep dwelling *and keep the original start time*. See decision 1.
      if (isWithin(event.point, state.rect, config.slopPx)) return { state, effect: null };

      return {
        state: { kind: 'held' },
        effect: { kind: 'cancelled', targetId: state.targetId, reason: 'moved' },
      };
    }

    case 'tick': {
      if (state.kind !== 'dwelling') return { state, effect: null };
      if (event.atMs - state.startedAtMs < config.dwellMs) return { state, effect: null };

      // Completed. `held` prevents a second activation from the same unbroken press — decision 2.
      return { state: { kind: 'held' }, effect: { kind: 'activate', targetId: state.targetId } };
    }

    case 'up': {
      if (state.kind === 'dwelling') {
        // Lifted early. Not an activation: dwell's whole promise is that holding is what selects, and
        // activating on a short press as well would make it indistinguishable from direct touch.
        return {
          state: idleDwell(),
          effect: { kind: 'cancelled', targetId: state.targetId, reason: 'released' },
        };
      }
      return { state: idleDwell(), effect: null };
    }
  }
}

/**
 * How far through the dwell, 0..1 — for the radial indicator.
 *
 * Here rather than in the component because it is the same arithmetic the machine uses to decide
 * completion, and two copies of it would let the ring finish at a different moment from the selection.
 */
export function dwellProgress(state: DwellState, nowMs: number, dwellMs: number): number {
  if (state.kind !== 'dwelling' || dwellMs <= 0) return 0;
  return Math.min(1, Math.max(0, (nowMs - state.startedAtMs) / dwellMs));
}
